import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { restaurantInfo } from "../mock";

const contactInfo = [
  { id: "address", icon: <MapPin size={20} />, label: "Address", value: `${restaurantInfo.address.line1}, ${restaurantInfo.address.line2}` },
  { id: "phone", icon: <Phone size={20} />, label: "Phone", value: restaurantInfo.phoneDisplay, link: `tel:${restaurantInfo.phone}` },
  { id: "email", icon: <Mail size={20} />, label: "Email", value: restaurantInfo.email, link: `mailto:${restaurantInfo.email}` },
  { id: "hours", icon: <Clock size={20} />, label: "Hours", value: "Mon–Sun 6:30 AM – 11:30 PM " }
];

export function ContactInfo() {
  return (
    <div>
      <span className="divider-ornament">Visit Us</span>
      <h2 className="font-display text-sea-deep text-4xl md:text-5xl mt-4 mb-5 leading-tight">
        Come say <em className="italic text-spice-gold font-light">hello</em>
      </h2>
      <p className="text-sea-mid/80 leading-relaxed mb-10">
        Find us in the heart of Frankston. Whether it&apos;s a romantic dinner, a family celebration or a business gathering, we look forward to welcoming you.
      </p>

      <div className="space-y-6">
        {contactInfo.map((info) => (
          <div key={info.id} className="flex items-start gap-5 pb-6 border-b border-sea-deep/10">
            <div className="w-12 h-12 bg-sea-deep text-spice-gold flex items-center justify-center flex-shrink-0">
              {info.icon}
            </div>
            <div>
              <div className="text-spice-gold text-[10px] tracking-[0.3em] uppercase mb-1">{info.label}</div>
              {info.link ? (
                <a href={info.link} className="text-sea-deep hover:text-spice-gold transition-colors">{info.value}</a>
              ) : (
                <div className="text-sea-deep">{info.value}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-8">
        <a href="#" aria-label="Instagram" className="w-11 h-11 border border-sea-deep/20 hover:bg-sea-deep hover:text-spice-gold hover:border-sea-deep flex items-center justify-center transition-all text-sea-deep">
          <Instagram size={18} />
        </a>
        <a href="https://www.facebook.com/61592026056651/videos/-sea-spice-kitchen-frankstonyour-all-day-spot-for-breakfast-brunch-lunch-dinner-/1522493162530860/?mibextid=wwXIfr&rdid=kZS7Lu4NwhD3AsnL" target="_blank" rel="noreferrer" aria-label="Facebook" className="w-11 h-11 border border-sea-deep/20 hover:bg-sea-deep hover:text-spice-gold hover:border-sea-deep flex items-center justify-center transition-all text-sea-deep">
          <Facebook size={18} />
        </a>
      </div>
    </div>
  );
}

const initialForm = { name: "", email: "", subject: "", message: "" };

function persistMessage(payload) {
  const existing = JSON.parse(localStorage.getItem("sea_spice_messages") || "[]");
  localStorage.setItem("sea_spice_messages", JSON.stringify([...existing, payload]));
}

export function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submitlocalstorage = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    persistMessage({ ...form, at: new Date().toISOString() });
    toast.success("Thanks! We'll get back to you within 24 hours.");
    setForm(initialForm);
  };
const submit = async (e) => {
  e.preventDefault();

  if (!form.name || !form.email || !form.message) {
    toast.error("Please fill in all required fields");
    return;
  }
const API_URL = process.env.REACT_APP_API_URL || "";

  try {
    const response = await fetch(`${API_URL}/api/contact`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: form.name,
        email: form.email,
        phone: "",
        subject: form.subject,
        message: form.message,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to send message");
    }

    toast.success("Thanks! We'll get back to you within 24 hours.");
    setForm(initialForm);
  } catch (error) {
    console.error(error);
    toast.error("Unable to send message. Please try again.");
  }
};
  return (
    <form onSubmit={submit} className="bg-white p-8 lg:p-10 border border-sea-deep/10 space-y-5">
      <h3 className="font-display text-sea-deep text-2xl mb-2">Send us a message</h3>
      <p className="text-sea-mid/70 text-sm mb-3">We&apos;ll respond within 24 hours.</p>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="c-name" className="text-sea-deep text-sm">Name *</Label>
          <Input id="c-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" />
        </div>
        <div>
          <Label htmlFor="c-email" className="text-sea-deep text-sm">Email *</Label>
          <Input id="c-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" />
        </div>
      </div>
      <div>
        <Label htmlFor="c-subject" className="text-sea-deep text-sm">Subject</Label>
        <Input id="c-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" placeholder="Private event enquiry" />
      </div>
      <div>
        <Label htmlFor="c-message" className="text-sea-deep text-sm">Message *</Label>
        <Textarea id="c-message" value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-2 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold min-h-[140px]" placeholder="How can we help?" />
      </div>
      <Button type="submit" className="w-full bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold rounded-none h-14 tracking-wide">
        Send Message
      </Button>
    </form>
  );
}
